import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import { getFullName } from 'utils/personUtils';
import HistoryLog from '../../../HistoryLog';
import HistoryField from '../../../HistoryField';
import {
  AssignmentHistoryResponse,
  AssignmentHistoryDetailsModel,
  AssignmentContactPersonEditModel,
  AssignmentTimesheetApproverInfoViewModel,
  AssignmentCategoryEnum,
  AssignmentBillingContactEditModel,
} from 'api/adminApiClient';
import {
  PreferredContactMethodEnum as BackofficePreferredContactMethodEnum,
  PrimaryContactForEnum as BackofficePrimaryContactForEnum,
} from 'api/backofficeApiClient';
import { getAssignmentTimesheetApprovers } from 'utils/assignmentUtils';
import {
  getPrimaryContactsMaxLength,
  hasAnyPrimaryContactsDisplayableChange,
  hasPrimaryContactFieldChanged,
  PrimaryContactFieldKey,
} from 'utils/primaryContactHistoryUtils';
import {
  getContactForLabel,
  getPreferredContactMethodLabel,
} from 'utils/primaryContactUtils';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export default function CustomerInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;

  //General information
  const hasSelectCustomerChanged = !isEqual(
    oldData?.customerCompanyName || '',
    newData?.customerCompanyName || '',
  );
  const oldBestPrimaryContacts = oldData?.bestPrimaryContacts;
  const newBestPrimaryContacts = newData?.bestPrimaryContacts;
  const hasBestPrimaryContactsChanged = hasAnyPrimaryContactsDisplayableChange(
    oldBestPrimaryContacts,
    newBestPrimaryContacts,
  );
  const hasBestBillingContactsChanged =
    oldData?.bestBillingContacts &&
    oldData?.bestBillingContacts.length > 0 &&
    newData?.bestBillingContacts &&
    newData?.bestBillingContacts.length > 0 &&
    !isEqual(oldData?.bestBillingContacts, newData?.bestBillingContacts);

  const hasGeneralInformationChanged =
    hasSelectCustomerChanged ||
    hasBestPrimaryContactsChanged ||
    hasBestBillingContactsChanged;

  //Supervisor Info
  const hasSupervisorFirstNameChanged = !isEqual(
    oldData?.supervisorInfo?.firstName || '',
    newData?.supervisorInfo?.firstName || '',
  );
  const hasSupervisorLastNameChanged = !isEqual(
    oldData?.supervisorInfo?.lastName || '',
    newData?.supervisorInfo?.lastName || '',
  );
  const hasSupervisorEmailChanged = !isEqual(
    oldData?.supervisorInfo?.email || '',
    newData?.supervisorInfo?.email || '',
  );
  const hasSupervisorPhoneNumberChanged = !isEqual(
    oldData?.supervisorInfo?.phoneNumber || '',
    newData?.supervisorInfo?.phoneNumber || '',
  );
  const hasSupervisorTitleChanged = !isEqual(
    oldData?.supervisorInfo?.title || '',
    newData?.supervisorInfo?.title || '',
  );

  const hasAdditionalTimesheetApproverChanged = !isEqual(
    oldData?.additionalTimesheetApprovers || [],
    newData?.additionalTimesheetApprovers || [],
  );
  const hasSupervisorInformation =
    hasSupervisorFirstNameChanged ||
    hasSupervisorLastNameChanged ||
    hasSupervisorEmailChanged ||
    hasSupervisorPhoneNumberChanged ||
    hasSupervisorTitleChanged;

  //Timesheet approver information
  const hasTSApproverFirstNameChanged = !isEqual(
    oldData?.timesheetApproverInfo?.firstName || '',
    newData?.timesheetApproverInfo?.firstName || '',
  );
  const hasTSApproverLastNameChanged = !isEqual(
    oldData?.timesheetApproverInfo?.lastName || '',
    newData?.timesheetApproverInfo?.lastName || '',
  );
  const hasTSApproverEmailChanged = !isEqual(
    oldData?.timesheetApproverInfo?.email || '',
    newData?.timesheetApproverInfo?.email || '',
  );
  const hasTSApproverPhoneNumberChanged = !isEqual(
    oldData?.timesheetApproverInfo?.phoneNumber || '',
    newData?.timesheetApproverInfo?.phoneNumber || '',
  );
  const hasTSApproverNotApplicableChanged = !isEqual(
    oldData?.timesheetApproverNotApplicable || '',
    newData?.timesheetApproverNotApplicable || '',
  );
  const hasTimesheetApproverInfoFieldsChanged =
    hasTSApproverFirstNameChanged ||
    hasTSApproverLastNameChanged ||
    hasTSApproverEmailChanged ||
    hasTSApproverPhoneNumberChanged;

  const hasTimesheetApproverInformationCnanged =
    hasTimesheetApproverInfoFieldsChanged ||
    hasAdditionalTimesheetApproverChanged;

  //Timesheet backup approver information
  const hasTSBackupApproverFirstNameChanged = !isEqual(
    oldData?.timesheetBackupApproverInfo?.firstName || '',
    newData?.timesheetBackupApproverInfo?.firstName || '',
  );
  const hasTSBackupApproverLastNameChanged = !isEqual(
    oldData?.timesheetBackupApproverInfo?.lastName || '',
    newData?.timesheetBackupApproverInfo?.lastName || '',
  );
  const hasTSBackupApproverEmailChanged = !isEqual(
    oldData?.timesheetBackupApproverInfo?.email || '',
    newData?.timesheetBackupApproverInfo?.email || '',
  );
  const hasTSBackupApproverPhoneNumberChanged = !isEqual(
    oldData?.timesheetBackupApproverInfo?.phoneNumber || '',
    newData?.timesheetBackupApproverInfo?.phoneNumber || '',
  );
  const hasTimesheetBackupInformationChanged =
    hasTSBackupApproverFirstNameChanged ||
    hasTSBackupApproverLastNameChanged ||
    hasTSBackupApproverEmailChanged ||
    hasTSBackupApproverPhoneNumberChanged;

  const hasBestPrimaryContactFieldChanged = (
    index: number,
    field: PrimaryContactFieldKey,
  ): boolean =>
    hasPrimaryContactFieldChanged(
      oldBestPrimaryContacts,
      newBestPrimaryContacts,
      index,
      field,
    );

  const renderGeneralInformationSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const {
      bestPrimaryContacts,
      assignmentCategory,
      bestBillingContacts,
      customerCompanyName,
    } = assignment;
    const isCorporateTA =
      assignmentCategory === AssignmentCategoryEnum.Corporate;

    const sectionName = isCorporateTA
      ? 'primary-contact'
      : 'general-information';
    const bestPrimaryContactsMaxLength = getPrimaryContactsMaxLength(
      oldBestPrimaryContacts,
      newBestPrimaryContacts,
    );

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              {isCorporateTA ? 'Primary contact' : 'General information'}
            </div>
          </Col>
          {hasSelectCustomerChanged && (
            <HistoryField
              title="Customer"
              value={customerCompanyName}
              dataId={`${sectionName}-customer-field`}
            />
          )}
          <Col xs={24}>
            {hasBestPrimaryContactsChanged &&
              Array.from(
                { length: bestPrimaryContactsMaxLength },
                (_, index) => {
                  const contact: AssignmentContactPersonEditModel | undefined =
                    bestPrimaryContacts?.[index];
                  const hasNameChanged =
                    hasBestPrimaryContactFieldChanged(index, 'firstName') ||
                    hasBestPrimaryContactFieldChanged(index, 'lastName');
                  const hasTitleChanged = hasBestPrimaryContactFieldChanged(
                    index,
                    'title',
                  );
                  const hasEmailChanged = hasBestPrimaryContactFieldChanged(
                    index,
                    'email',
                  );
                  const hasPhoneNumberChanged =
                    hasBestPrimaryContactFieldChanged(index, 'phoneNumber');
                  const hasPreferredContactMethodChanged =
                    hasBestPrimaryContactFieldChanged(
                      index,
                      'preferredContactMethod',
                    );
                  const hasContactForChanged =
                    hasBestPrimaryContactFieldChanged(index, 'contactFor');
                  const showContactBlock =
                    hasNameChanged ||
                    hasTitleChanged ||
                    hasEmailChanged ||
                    hasPhoneNumberChanged ||
                    hasPreferredContactMethodChanged ||
                    hasContactForChanged;

                  if (!showContactBlock) {
                    return null;
                  }

                  return (
                    <React.Fragment key={`${index}`}>
                      <Row gutter={[10, 10]}>
                        {hasNameChanged && (
                          <HistoryField
                            title="Primary contact"
                            value={getFullName(
                              contact?.firstName,
                              contact?.lastName,
                            )}
                            dataId={`${sectionName}-primary-contact-field-${index}`}
                          />
                        )}

                        {hasTitleChanged && (
                          <HistoryField
                            title="Title"
                            value={contact?.title}
                            dataId={`${sectionName}-title-field-${index}`}
                          />
                        )}

                        {hasEmailChanged && (
                          <HistoryField
                            title="Email"
                            value={contact?.email}
                            dataId={`${sectionName}-email-field-${index}`}
                          />
                        )}

                        {hasPhoneNumberChanged && (
                          <HistoryField
                            title="Phone number"
                            value={contact?.phoneNumber}
                            dataId={`${sectionName}-phone-number-field-${index}`}
                          />
                        )}

                        {hasPreferredContactMethodChanged && (
                          <HistoryField
                            title="Preferred Contact Method"
                            value={getPreferredContactMethodLabel(
                              contact?.preferredContactMethod as unknown as
                                | BackofficePreferredContactMethodEnum[]
                                | undefined,
                            )}
                            dataId={`${sectionName}-preferred-contact-method-field-${index}`}
                          />
                        )}

                        {hasContactForChanged && (
                          <HistoryField
                            title="Contact For"
                            value={getContactForLabel(
                              contact?.contactFor as unknown as
                                | BackofficePrimaryContactForEnum[]
                                | undefined,
                            )}
                            dataId={`${sectionName}-contact-for-field-${index}`}
                          />
                        )}
                      </Row>

                      <Divider />
                    </React.Fragment>
                  );
                },
              )}
          </Col>
          <Col xs={24}>
            {bestBillingContacts &&
              (bestBillingContacts || []).map(
                (contact: AssignmentBillingContactEditModel, index: number) => (
                  <React.Fragment key={`${index}`}>
                    <Row gutter={[10, 10]}>
                      <HistoryField
                        title="Billing contact"
                        value={getFullName(
                          contact?.firstName,
                          contact?.lastName,
                        )}
                        dataId={`${sectionName}-billing-contact-field`}
                      />
                      <HistoryField
                        title="Email"
                        value={contact?.email}
                        dataId={`${sectionName}-email-field`}
                      />
                      <HistoryField
                        title="Phone number"
                        value={contact?.phoneNumber}
                        dataId={`${sectionName}-phone-number-field`}
                      />
                    </Row>

                    <Divider />
                  </React.Fragment>
                ),
              )}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const renderSupervisorInformationSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { supervisorInfo } = assignment;
    const sectionName = 'supervisor-information';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Supervisor information</div>
          </Col>
          {hasSupervisorFirstNameChanged && (
            <HistoryField
              title="First name"
              value={supervisorInfo?.firstName}
              dataId={`${sectionName}-first-name-field`}
            />
          )}
          {hasSupervisorLastNameChanged && (
            <HistoryField
              title="Last name"
              value={supervisorInfo?.lastName}
              dataId={`${sectionName}-last-name-field`}
            />
          )}
          {hasSupervisorEmailChanged && (
            <HistoryField
              title="Email"
              value={supervisorInfo?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasSupervisorPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={supervisorInfo?.phoneNumber}
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
          {hasSupervisorTitleChanged && (
            <HistoryField
              title="Title"
              value={supervisorInfo?.title}
              dataId={`${sectionName}-title-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAdditionalTimesheetApprovers = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { timesheetApproverNotApplicable } = assignment;

    const timesheetApprovers = getAssignmentTimesheetApprovers(assignment);

    const additionalTimesheetApprovers: AssignmentTimesheetApproverInfoViewModel[] =
      timesheetApprovers.length
        ? timesheetApprovers
        : [
            {
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
            },
          ];

    const sectionName = 'additional-timesheet-approvers';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              Timesheet approver information
            </div>
          </Col>
          {hasTSApproverNotApplicableChanged && (
            <HistoryField
              title="Timesheet approver"
              value={
                timesheetApproverNotApplicable ? 'Not applicable' : 'Applicable'
              }
              dataId={`${sectionName}-timesheet-approver-field`}
            />
          )}
          <Col xs={24}>
            {hasTimesheetApproverInformationCnanged &&
              additionalTimesheetApprovers.map(
                (
                  timesheetApproverInfo: AssignmentTimesheetApproverInfoViewModel,
                  index: number,
                ) => (
                  <React.Fragment key={`${index}`}>
                    <Row gutter={[10, 10]}>
                      <HistoryField
                        title="First name"
                        value={timesheetApproverInfo?.firstName}
                        dataId={`${sectionName}-first-name-field`}
                      />
                      <HistoryField
                        title="Last name"
                        value={timesheetApproverInfo?.lastName}
                        dataId={`${sectionName}-last-name-field`}
                      />
                      <HistoryField
                        title="Email"
                        value={timesheetApproverInfo?.email}
                        dataId={`${sectionName}-email-field`}
                      />
                      <HistoryField
                        title="Phone number"
                        value={timesheetApproverInfo?.phoneNumber}
                        dataId={`${sectionName}-phone-number-field`}
                      />
                    </Row>

                    <Divider />
                  </React.Fragment>
                ),
              )}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const renderTSBackupApproverSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { timesheetBackupApproverInfo } = assignment;
    const sectionName = 'timesheet-backup-info';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Timesheet backup information</div>
          </Col>
          {hasTSBackupApproverFirstNameChanged && (
            <HistoryField
              title="First name"
              value={timesheetBackupApproverInfo?.firstName}
              dataId={`${sectionName}-first-name-field`}
            />
          )}
          {hasTSBackupApproverLastNameChanged && (
            <HistoryField
              title="Last name"
              value={timesheetBackupApproverInfo?.lastName}
              dataId={`${sectionName}-last-name-field`}
            />
          )}
          {hasTSBackupApproverEmailChanged && (
            <HistoryField
              title="Email"
              value={timesheetBackupApproverInfo?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasTSBackupApproverPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={timesheetBackupApproverInfo?.phoneNumber}
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderCustomerInformation = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    return (
      <>
        {hasGeneralInformationChanged &&
          renderGeneralInformationSection(assignment)}
        {hasSupervisorInformation &&
          renderSupervisorInformationSection(assignment)}
        {(hasTimesheetApproverInformationCnanged ||
          hasTSApproverNotApplicableChanged) &&
          renderAdditionalTimesheetApprovers(assignment)}
        {hasTimesheetBackupInformationChanged &&
          renderTSBackupApproverSection(assignment)}
      </>
    );
  };

  if (
    !hasGeneralInformationChanged &&
    !hasSupervisorInformation &&
    !hasAdditionalTimesheetApproverChanged &&
    !hasTimesheetApproverInformationCnanged &&
    !hasTSApproverNotApplicableChanged &&
    !hasTimesheetBackupInformationChanged
  )
    return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderCustomerInformation(oldData)}
      newData={renderCustomerInformation(newData)}
    />
  );
}
